import { useContext, type CSSProperties } from 'react';
import { ThemeContext } from '@src/theme/ThemeContext';
import type { Season } from '@src/theme/season';
import { testIdAttr } from '@src/components/testId';
import './Confetti.css';

type PieceStyle = CSSProperties & {
  '--piece-left': string;
  '--piece-color': string;
  '--piece-size': string;
  '--piece-spin': string;
  '--piece-duration': string;
  '--piece-delay': string;
  '--piece-drift': string;
  '--burst-x': string;
  '--burst-rise': string;
  '--burst-duration': string;
  '--burst-delay': string;
  '--burst-front-delay': string;
};

const PIECE_COUNT = 140;

// Each season keeps its own celebration materials in both light and dark mode.
const PALETTES: Record<Season, readonly string[]> = {
  alpine: ['#d85053', '#f5cc69', '#4a8f62', '#8bcae5', '#fff5dc'],
  candy: ['#ffb4d8', '#ffed9a', '#8acb53', '#7fd8ed', '#f77db2', '#fff4e7'],
  egypt: ['#ffecac', '#e7b64e', '#c88a2e', '#f7d777', '#fff2c6', '#dba344'],
  winter: ['#d85053', '#f5cc69', '#4a8f62', '#8bcae5', '#fff5dc'],
  halloween: ['#ff9b3d', '#d87927', '#9f7bd6', '#e5c4ff', '#ffe2a0'],
  'cherry-blossom': ['#ef91b2', '#ffc6dc', '#fff0f5', '#db78a1', '#f4cc8c'],
  pixel: ['#ff776e', '#ffdf6b', '#70d6a0', '#69c5ff', '#bc98ff'],
};

function mulberry32(seed: number) {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* Generated once at module load from a fixed seed, so SSR and the client agree
 * and a re-render never reshuffles a fall that is already in flight. */
const PIECES = Array.from({ length: PIECE_COUNT }, (_, index) => {
  const random = mulberry32(index + 7);
  const piece = {
    left: `${(random() * 100).toFixed(2)}%`,
    colorIndex: random(),
    size: `${(0.35 + random() * 0.45).toFixed(3)}rem`,
    spin: `${Math.round(random() * 1080 - 540)}deg`,
    duration: `${(0.8 + random() * 0.45).toFixed(2)}`,
    delay: `${(random() * 0.7).toFixed(2)}s`,
    drift: `${Math.round(random() * 160 - 80)}px`,
    // A few narrower arcs return across the card, even on wide screens.
    burstX: `${(index % 2 ? 1 : -1) * (index % 6 < 2 ? 5 + random() * 18 : 24 + random() * 62)}vw`,
    burstRise: 55 + random() * 35,
    burstDuration: 2.6 + random() * 1.2,
    burstDelay: random() * 0.16,
  };
  // The crest of y = -4r*t*(1-t) + 80*t² is at t = 2r/(4r+80).
  // Move each piece in front only when its own upward flight turns downward.
  return {
    ...piece,
    burstFrontDelay:
      piece.burstDelay + (piece.burstDuration * (2 * piece.burstRise)) / (4 * piece.burstRise + 80),
  };
});

export interface ConfettiProps {
  /** Mount it to celebrate; unmount it when the moment has passed. */
  active: boolean;
  testId?: string;
  variant?: 'fall' | 'burst';
}

/** Paper over the whole viewport, for the one moment that deserves it. */
export function Confetti({ active, testId, variant = 'fall' }: ConfettiProps) {
  const season = useContext(ThemeContext)?.season ?? 'winter';
  const colors = PALETTES[season];
  if (!active) {
    return null;
  }

  return (
    <div
      className={`confetti confetti--${variant}`}
      data-season={season}
      aria-hidden="true"
      {...testIdAttr(testId)}
    >
      {(variant === 'burst' ? PIECES : PIECES.slice(0, 90)).map((piece, index) => (
        <span
          key={index}
          className="confetti__piece"
          style={
            {
              '--piece-left': piece.left,
              '--piece-color': colors[Math.floor(piece.colorIndex * colors.length)],
              '--piece-size': piece.size,
              '--piece-spin': piece.spin,
              '--piece-duration': piece.duration,
              '--piece-delay': piece.delay,
              '--piece-drift': piece.drift,
              '--burst-x': piece.burstX,
              '--burst-rise': `${piece.burstRise}dvh`,
              '--burst-duration': `${piece.burstDuration}s`,
              '--burst-delay': `${piece.burstDelay}s`,
              '--burst-front-delay': `${piece.burstFrontDelay}s`,
            } as PieceStyle
          }
        />
      ))}
    </div>
  );
}
